import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faPaw,
  faCoffee,
  faIdCard,
  faMapMarkerAlt,
  faClock,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import { getCanonicalUrl } from '../../../utils/seo';

const DOG_PARK_BOOKING =
  '/booking-journey?locationId=978&pageId=db0a4790-c484-474b-b350-e7ac8209f9e7';

const IMG_HERO = 'https://BelvoirCastlePull.b-cdn.net/Website/Dog%20Park%201.jpeg';

const C = {
  navy: '#101d40',
  navyDeep: '#0a1329',
  bronze: '#9d6d49',
  bronzeSoft: '#c4a07e',
  ivory: '#f8f7f4',
  parchment: '#f3efe6',
  ink: '#2c3340',
  mute: '#5c6370',
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;
  color: ${C.ink};

  @media (max-width: 768px) {
    padding-top: clamp(188px, 34vw, 228px);
  }
`;

const Hero = styled.section`
  position: relative;
  min-height: clamp(560px, 86vh, 820px);
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  color: #fff;
`;

const HeroImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 35%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(
      to top,
      rgba(10, 19, 41, 0.94) 0%,
      rgba(16, 29, 64, 0.72) 32%,
      rgba(16, 29, 64, 0.28) 62%,
      rgba(16, 29, 64, 0.1) 100%
    );
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: clamp(2.5rem, 6vw, 4.5rem) clamp(1.25rem, 5vw, 3rem) clamp(3rem, 7vw, 5rem);
  animation: ${fadeUp} 0.75s ease both;
`;

const BrandWord = styled.p`
  margin: 0 0 0.35rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.4rem, 6.5vw, 4.6rem);
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1;
`;

const BrandRule = styled.span`
  display: block;
  width: 3.5rem;
  height: 2px;
  background: ${C.bronze};
  margin: 0.85rem 0 1.1rem;
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.85rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.55rem, 3.4vw, 2.35rem);
  font-weight: 500;
  font-style: italic;
  line-height: 1.2;
  max-width: 28ch;
`;

const HeroLead = styled.p`
  margin: 0 0 1.75rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.7vw, 1.08rem);
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.86);
  max-width: 38rem;
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
`;

const Btn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 3.05rem;
  padding: 0.85rem 1.65rem;
  border-radius: 2px;
  text-decoration: none;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.18s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const BtnPrimary = styled(Btn)`
  background: ${C.bronze};
  color: #fff;
  border: 1px solid ${C.bronze};

  &:hover {
    background: ${C.bronzeSoft};
    border-color: ${C.bronzeSoft};
    color: ${C.navy};
  }
`;

const BtnGhost = styled(Btn)`
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.45);

  &:hover {
    border-color: #fff;
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
  }
`;

const ComingSoonNote = styled.span`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${C.bronzeSoft};
  margin-left: 0.15rem;
`;

const Section = styled.section`
  padding: clamp(3.25rem, 6.5vw, 5.5rem) clamp(1.25rem, 5vw, 3rem);
  background: ${p => p.$bg || C.ivory};
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  animation: ${fadeUp} 0.65s ease both;
`;

const Kicker = styled.p`
  margin: 0 0 0.7rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${C.bronze};
`;

const Divider = styled.div`
  width: 48px;
  height: 2px;
  background: ${C.bronze};
  margin-bottom: 1.2rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 1.15rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2rem, 4.2vw, 2.9rem);
  font-weight: 500;
  line-height: 1.15;
  color: ${C.navy};
`;

const Body = styled.p`
  margin: 0 0 1rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 1.02rem;
  line-height: 1.75;
  color: ${C.mute};
  max-width: 44rem;

  &:last-child {
    margin-bottom: 0;
  }

  a {
    color: ${C.bronze};
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1.1fr 0.9fr'};
  gap: clamp(2rem, 5vw, 3.5rem);
  align-items: ${p => p.$align || 'start'};

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const ReasonList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1.35rem;
`;

const ReasonItem = styled.li`
  display: grid;
  grid-template-columns: 2.6rem 1fr;
  gap: 1rem;
  align-items: start;
`;

const ReasonIcon = styled.div`
  width: 2.6rem;
  height: 2.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${C.navy};
  color: ${C.bronzeSoft};
  font-size: 1rem;
`;

const ReasonTitle = styled.h3`
  margin: 0 0 0.35rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.35rem;
  font-weight: 600;
  color: ${C.navy};
`;

const ReasonText = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.96rem;
  line-height: 1.65;
  color: ${C.mute};
`;

const OfferPanel = styled.div`
  background: ${C.navy};
  color: #fff;
  padding: clamp(2rem, 4vw, 2.75rem);
`;

const OfferPrice = styled.p`
  margin: 0 0 0.35rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.4rem, 4vw, 3.2rem);
  font-weight: 600;
  line-height: 1;
  color: #fff;
`;

const OfferMeta = styled.p`
  margin: 0 0 1.5rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.95rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.78);
`;

const HoursPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: rgba(157, 109, 73, 0.35);
  border: 1px solid rgba(157, 109, 73, 0.35);

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const HoursCell = styled.div`
  background: ${C.parchment};
  padding: 1.5rem 1.4rem;
`;

const HoursLabel = styled.p`
  margin: 0 0 0.4rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${C.bronze};
`;

const HoursValue = styled.p`
  margin: 0;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.45rem;
  font-weight: 600;
  color: ${C.navy};
`;

const LocationBand = styled.section`
  background: ${C.navyDeep};
  color: #fff;
  padding: clamp(3rem, 6vw, 4.5rem) clamp(1.25rem, 5vw, 3rem);
`;

const LocationInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: clamp(1.75rem, 4vw, 3rem);
  align-items: center;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const LocationTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.9rem, 3.8vw, 2.6rem);
  font-weight: 500;
  color: #fff;
`;

const LocationBody = styled.p`
  margin: 0 0 1rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 1.02rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.8);

  &:last-child {
    margin-bottom: 0;
  }
`;

const AddressBox = styled.div`
  padding: 1.75rem 1.5rem;
  border: 1px solid rgba(196, 160, 126, 0.4);
  background: rgba(255, 255, 255, 0.04);
`;

const AddressLabel = styled.p`
  margin: 0 0 0.65rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${C.bronzeSoft};
`;

const AddressText = styled.p`
  margin: 0;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.35rem;
  line-height: 1.45;
  color: #fff;
`;

const RelatedLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem 1.5rem;
  margin-top: 1.75rem;
`;

const RelatedLink = styled(Link)`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  color: ${C.bronzeSoft};
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: #fff;
  }
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba(16, 29, 64, 0.12);
`;

const FAQItem = styled.div`
  border-bottom: 1px solid rgba(16, 29, 64, 0.12);
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 1.25rem 0;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.2rem;
  font-weight: 600;
  color: ${C.navy};
  transition: color 0.2s ease;

  &:hover {
    color: ${C.bronze};
  }
`;

const FAQIcon = styled.span`
  color: ${C.bronze};
  transition: transform 0.25s ease;
  transform: ${p => (p.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  flex-shrink: 0;
`;

const FAQAnswer = styled.div`
  max-height: ${p => (p.$isOpen ? '400px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease, padding 0.3s ease;
  padding: ${p => (p.$isOpen ? '0 0 1.25rem' : '0')};
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.98rem;
  line-height: 1.7;
  color: ${C.mute};
`;

const ClubBand = styled.section`
  padding: clamp(3rem, 6vw, 4.5rem) clamp(1.25rem, 5vw, 3rem);
  background:
    linear-gradient(135deg, rgba(16, 29, 64, 0.92), rgba(16, 29, 64, 0.78)),
    url(${IMG_HERO}) center / cover no-repeat;
  color: #fff;
  text-align: center;
`;

const ClubInner = styled.div`
  max-width: 640px;
  margin: 0 auto;
`;

const ClubTitle = styled.h2`
  margin: 0 0 0.85rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.9rem, 3.8vw, 2.6rem);
  font-weight: 500;
`;

const ClubBody = styled.p`
  margin: 0 0 1.5rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 1.02rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.85);
`;

const ClubActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  align-items: center;
`;

const faqs = [
  {
    q: 'Do I need to bring my own water and poo bags?',
    a: "Poo bags are provided on site, but you're welcome to bring your own if you prefer. Fresh water is provided outside Belvoir Pet Shop at the Retail Village.",
  },
  {
    q: 'Is the field suitable for puppies?',
    a: "Yes — the secure, enclosed field makes it a great space for puppies to explore safely off the lead, though we'd recommend puppies are fully vaccinated before their first visit.",
  },
  {
    q: 'Can I bring more than two dogs?',
    a: "Yes, please select how many dogs you're bringing when you book your entry slot.",
  },
  {
    q: 'Where can I park?',
    a: 'When you book online, free parking is included in the main Castle car park. The Dog Park is next to the main visitor car park — the entrance is on Woolsthorpe Road.',
  },
  {
    q: "What happens if it's raining or muddy?",
    a: "The field remains open in all but the most extreme weather. As it's a grass field, please be prepared for muddy conditions after rain.",
  },
];

const reasons = [
  {
    icon: faShieldAlt,
    title: 'Secure, off-lead field',
    text: 'Fully enclosed for total peace of mind — ideal for nervous or reactive dogs, high-energy breeds, or recall training.',
  },
  {
    icon: faPaw,
    title: 'Suitable for all dogs',
    text: 'From confident explorers to nervous first-timers, the field offers a safe space to run, play and explore.',
  },
  {
    icon: faCoffee,
    title: 'Minutes from Belvoir Bistro',
    text: "Just a minute's walk from Belvoir Bistro — the perfect dog-friendly pitstop for coffee and a bite before or after your session.",
  },
  {
    icon: faIdCard,
    title: 'Memberships coming soon',
    text: 'Belvoir Dog Club is coming soon for regular visitors, with member perks across the Estate.',
  },
];

const DogPark = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq((current) => (current === index ? null : index));
  };

  return (
    <Page>
      <Helmet>
        <title>Belvoir Dog Park | Off-Lead Dog Field | Belvoir Castle</title>
        <meta
          name="description"
          content="Book Belvoir Castle's secure off-lead dog field by the hour. Sole use of an enclosed field near Belvoir Retail Village — ideal for all dogs."
        />
        <link rel="canonical" href={getCanonicalUrl('dog-park')} />
      </Helmet>

      <Hero>
        <HeroImage src={IMG_HERO} alt="Dogs enjoying Belvoir Dog Park" />
        <HeroOverlay />
        <HeroInner>
          <BrandWord>Belvoir Dog Park</BrandWord>
          <BrandRule />
          <HeroTitle>Freedom to run, play and explore off the lead</HeroTitle>
          <HeroLead>
            Belvoir Castle&apos;s secure, enclosed dog field is now open. Book by the hour and reserve sole use of the field for extra peace of mind.
          </HeroLead>
          <HeroActions>
            <BtnPrimary href={DOG_PARK_BOOKING}>Book a slot</BtnPrimary>
            <BtnGhost as={Link} to="/dog-club">
              Dog Club
              <ComingSoonNote> · Coming soon</ComingSoonNote>
            </BtnGhost>
          </HeroActions>
        </HeroInner>
      </Hero>

      <Section>
        <Inner>
          <TwoCol $align="center">
            <div>
              <Kicker>Now open</Kicker>
              <Divider />
              <SectionTitle>A secure off-lead field on the Estate</SectionTitle>
              <Body>
                Belvoir Castle&apos;s off-lead dog field gives dogs the freedom to run, play and explore safely off the lead. Set within the Estate&apos;s overflow car park, the field offers a secure, enclosed space — ideal for nervous or reactive dogs, high-energy breeds, or recall training.
              </Body>
              <Body>
                Book by the hour and reserve sole use of the field for extra peace of mind.
              </Body>
            </div>
            <OfferPanel>
              <Kicker style={{ color: C.bronzeSoft }}>Opening offer</Kicker>
              <OfferPrice>£10 per slot</OfferPrice>
              <OfferMeta>
                Plus £2 per slot for an additional dog.
                <br />
                Includes free parking in the main Castle car park when you book online.
              </OfferMeta>
              <BtnPrimary href={DOG_PARK_BOOKING} style={{ width: '100%' }}>
                Book the Dog Park
              </BtnPrimary>
            </OfferPanel>
          </TwoCol>
        </Inner>
      </Section>

      <Section $bg={C.parchment}>
        <Inner>
          <Kicker>Why visit?</Kicker>
          <Divider />
          <SectionTitle>Designed with dogs — and their owners — in mind</SectionTitle>
          <Body style={{ marginBottom: '2rem' }}>
            Belvoir Dog Park forms part of a growing programme of dog-friendly experiences at Belvoir Castle, alongside dog-friendly walking trails across the Estate, Belvoir Pet Shop at the Retail Village, and dog-friendly Belvoir Bistro.
          </Body>
          <ReasonList>
            {reasons.map((reason) => (
              <ReasonItem key={reason.title}>
                <ReasonIcon>
                  <FontAwesomeIcon icon={reason.icon} />
                </ReasonIcon>
                <div>
                  <ReasonTitle>{reason.title}</ReasonTitle>
                  <ReasonText>{reason.text}</ReasonText>
                </div>
              </ReasonItem>
            ))}
          </ReasonList>
        </Inner>
      </Section>

      <Section>
        <Inner>
          <TwoCol $cols="1fr 1fr" $align="center">
            <div>
              <Kicker>
                <FontAwesomeIcon icon={faClock} style={{ marginRight: '0.45rem' }} />
                Opening hours
              </Kicker>
              <Divider />
              <SectionTitle>Open every day</SectionTitle>
              <Body>
                Check the seasonal hours below when you book. Sessions are reserved by the hour so you have the field to yourselves.
              </Body>
            </div>
            <HoursPanel>
              <HoursCell>
                <HoursLabel>April – September</HoursLabel>
                <HoursValue>7am – 7pm</HoursValue>
              </HoursCell>
              <HoursCell>
                <HoursLabel>October – March</HoursLabel>
                <HoursValue>8am – 6pm</HoursValue>
              </HoursCell>
            </HoursPanel>
          </TwoCol>
        </Inner>
      </Section>

      <LocationBand>
        <LocationInner>
          <div>
            <Kicker style={{ color: C.bronzeSoft }}>
              <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '0.45rem' }} />
              Where is it?
            </Kicker>
            <LocationTitle>Easy to find, easy to park</LocationTitle>
            <LocationBody>
              Belvoir Castle&apos;s Dog Park is located in the overflow car park adjacent to Belvoir Retail Village and the main visitor car park. The entrance is on Woolsthorpe Road.
            </LocationBody>
            <LocationBody>
              Book online and enjoy free parking in the main Castle car park.
            </LocationBody>
            <RelatedLinks>
              <RelatedLink to="/about/dog-friendly">Dog-friendly Belvoir</RelatedLink>
              <RelatedLink to="/retail-village/shops/belvoir-pet-shop">Belvoir Pet Shop</RelatedLink>
              <RelatedLink to="/retail-village/dining/belvoir-bistro">Belvoir Bistro</RelatedLink>
              <RelatedLink to="/about/parking">Parking</RelatedLink>
            </RelatedLinks>
          </div>
          <AddressBox>
            <AddressLabel>Address</AddressLabel>
            <AddressText>
              Belvoir Retail Village<br />
              Leicestershire<br />
              NG32 1PA
            </AddressText>
          </AddressBox>
        </LocationInner>
      </LocationBand>

      <Section>
        <Inner>
          <Kicker>FAQs</Kicker>
          <Divider />
          <SectionTitle>Before you visit</SectionTitle>
          <FAQList>
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <FAQItem key={faq.q}>
                  <FAQQuestion
                    type="button"
                    onClick={() => toggleFaq(index)}
                    aria-expanded={isOpen}
                  >
                    {faq.q}
                    <FAQIcon $isOpen={isOpen}>
                      <FontAwesomeIcon icon={faChevronDown} />
                    </FAQIcon>
                  </FAQQuestion>
                  <FAQAnswer $isOpen={isOpen}>{faq.a}</FAQAnswer>
                </FAQItem>
              );
            })}
          </FAQList>
          <div style={{ marginTop: '2rem' }}>
            <BtnPrimary href={DOG_PARK_BOOKING}>Book your Dog Park slot</BtnPrimary>
          </div>
        </Inner>
      </Section>

      <ClubBand>
        <ClubInner>
          <Kicker style={{ color: C.bronzeSoft }}>Belvoir Dog Club</Kicker>
          <ClubTitle>Dog Club coming soon</ClubTitle>
          <ClubBody>
            Memberships will be available for regular visitors. Register your interest now to be among the first to hear when Belvoir Dog Club launches — with perks across the Dog Park, Pet Shop and Estate.
          </ClubBody>
          <ClubActions>
            <BtnPrimary as={Link} to="/dog-club">
              Register interest
            </BtnPrimary>
            <BtnGhost href={DOG_PARK_BOOKING}>Book a Dog Park slot</BtnGhost>
          </ClubActions>
        </ClubInner>
      </ClubBand>

      <Footer />
    </Page>
  );
};

export default DogPark;
